import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import mixpanel from "mixpanel-browser";

import { useInsights } from "../InsightsContext";

import { selectUserCredits, selectUserUid } from "store/users/selectors";
import { activateInsights } from "features/Model/_ducks/thunk";
import { usePaymentModal } from "contexts/PaymentModalContext.jsx";

import Dialog, { CloseDialog } from "components/Dialog";
import Button from "components/Button";

import InsightsSmall from "assets/images/icons/JSX/InsightsSmall";
import VoltaCreditBlue from "assets/images/icons/VoltaCreditBlue.svg";

import classes from "./styles.module.scss";

const ActivateInsightsModal = ({ open, modelId, modelName, userCredits, userId }) => {
    const dispatch = useDispatch();
    const { dispatch: paymentModalDispatch } = usePaymentModal();
    const { setIsActivateInsightsModalOpen } = useInsights();

    const [isActivating, setIsActivating] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const { credits = 0 } = userCredits || {};
    const hasEnoughCredits = credits >= 1;

    useEffect(() => {
        if (!open) return;
        mixpanel.track("Insights Activate Modal Open", {
            ModelId: modelId,
            UserId: userId,
        });
    }, [open, modelId, userId]);

    const onClose = () => {
        if (isActivating) return;

        setErrorMessage(null);
        setIsActivateInsightsModalOpen(false);
    };

    const openBuyCredits = () => {
        paymentModalDispatch({ type: "OPEN" });
    };

    const onActivateInsights = async () => {
        if (isActivating || !hasEnoughCredits) return;

        setIsActivating(true);
        setErrorMessage(null);

        try {
            await dispatch(activateInsights(modelId, modelName));
            mixpanel.track("Insights Activate", {
                ModelId: modelId,
                UserId: userId,
                ModelName: modelName,
            });
            setIsActivateInsightsModalOpen(false);
        } catch (error) {
            mixpanel.track("Insights Activate Fail", {
                ModelId: modelId,
                UserId: userId,
                Error: error?.message || "Failed to activate Insights",
            });
            setErrorMessage(error?.message || "Failed to activate Insights. Please try again.");
        } finally {
            setIsActivating(false);
        }
    };

    return (
        <Dialog open={open} closeActions={onClose} classes={{ paperClass: classes.activateInsightsModal }}>
            <CloseDialog closeActions={onClose} />
            <div className={classes.titleRow}>
                <span className={classes.iconBadge} aria-hidden="true">
                    <InsightsSmall fill="#0049C6" />
                </span>
                <h2 className={classes.title}>Activate Insights</h2>
            </div>
            <p className={classes.intro}>
                Spend <strong>1 credit</strong> to permanently unlock Insights
                {modelName ? (
                    <>
                        {" "}
                        for <strong>{modelName}</strong>
                    </>
                ) : (
                    " for this model"
                )}
                .
            </p>

            <div className={classes.summaryCard}>
                <p className={classes.summaryCardTitle}>What you get</p>
                <ul className={classes.summaryList}>
                    <li>Permanent access to Room-by-Room, EnergyPlus, and Utility Analysis features</li>
                    <li>
                        Your first parametric analysis from this model includes <strong>2,500 unlocked runs</strong>
                    </li>
                </ul>
            </div>

            <div className={classes.creditCountContainer}>
                <p className={classes.creditCount}>
                    Credits: {credits} <img src={VoltaCreditBlue} alt="" />
                </p>
                {!hasEnoughCredits && (
                    <button type="button" className={classes.buyCreditsLink} onClick={openBuyCredits}>
                        Buy Credits
                    </button>
                )}
            </div>

            {!hasEnoughCredits && (
                <p className={`${classes.alert} ${classes.alertError}`}>
                    You need <strong>1 credit</strong> to activate Insights.
                </p>
            )}

            {errorMessage && <p className={classes.errorText}>{errorMessage}</p>}

            <div className={classes.footer}>
                <Button type="red" onClick={onClose} disabled={isActivating}>
                    Cancel
                </Button>
                <Button onClick={onActivateInsights} disabled={!hasEnoughCredits || isActivating}>
                    {isActivating ? "Activating..." : "Spend 1 credit"}
                </Button>
            </div>
        </Dialog>
    );
};

const mapStateToProps = createStructuredSelector({
    userCredits: selectUserCredits,
    userId: selectUserUid,
});

export default connect(mapStateToProps)(ActivateInsightsModal);
